import { memo, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    Heading,
    Text,
    Button,
    Modal,
    HStack,
    VStack,
} from '@hst-horizon/horizon-ui';
import findScriptUrl from '../util/findScriptUrl';

const TIMEOUT = 60_000;

interface UpdateNotifierProps {
    /**
     * Substring of the main bundle's <script> src, e.g. '/index' or '/main-'.
     * Only external, same-origin scripts are considered, so extension and
     * third-party scripts are always skipped.
     */
    path?: string;
    /** How often to check for a new version, in ms. */
    interval?: number;
    /**
     * Whether to poll for new versions. The default is fixed when webshop is
     * built, so library consumers must pass their own, e.g. `!import.meta.env.DEV`.
     */
    enabled?: boolean;
}

const UpdateNotifier = ({
    path = '/index',
    interval = TIMEOUT,
    enabled = import.meta.env.MODE !== 'development',
}: UpdateNotifierProps) => {
    const [isUpToDate, setIsUpToDate] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [stop, setStop] = useState(false);

    useEffect(() => {
        if (stop || !enabled) {
            return;
        }
        const mainScriptUrl = findScriptUrl(path);
        if (!mainScriptUrl) {
            // Logged rather than thrown: an error thrown in an effect unmounts the consumer's whole app.
            // eslint-disable-next-line no-console
            console.error(
                `UpdateNotifier: no same-origin script matching "${path}" was found.`
            );
            return;
        }
        const updateCheckInterval = setInterval(() => {
            (async () => {
                const response = await fetch(mainScriptUrl, {
                    method: 'HEAD',
                    cache: 'reload',
                });
                if (response.status === 404) {
                    clearInterval(updateCheckInterval);
                    setIsUpToDate(false);
                }
            })().catch(error => {
                throw error;
            });
        }, interval);

        return () => {
            clearInterval(updateCheckInterval);
        };
    }, [enabled, interval, isUpToDate, path, stop]);

    const close = () => {
        setStop(true);
        setIsUpToDate(true);
    };

    if (isUpToDate || stop) {
        return null;
    }

    return (
        <Modal
            open
            disableDismiss
            onOpenChange={value => {
                if (!value) {
                    close();
                }
            }}
        >
            <VStack gap="small">
                <Heading h3>
                    <FormattedMessage id="New version available" />
                </Heading>
                <VStack gap="medium">
                    <Text primary>
                        <FormattedMessage id="Please refresh the page to get the latest version." />
                    </Text>
                    <HStack gap="medium" justify="spaceBetween" align="center">
                        <Button secondary onClick={close}>
                            <FormattedMessage id="Later" />
                        </Button>
                        <Button
                            loading={isLoading}
                            onClick={() => {
                                window.location.reload();
                                setIsLoading(true);
                            }}
                        >
                            <FormattedMessage id="Refresh" />
                        </Button>
                    </HStack>
                </VStack>
            </VStack>
        </Modal>
    );
};

export default memo(UpdateNotifier);
